import { useQuery } from "@tanstack/react-query";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { api } from "../lib/api";
import type { Item } from "../lib/work";

const LIMIT = 12;

/**
 * Search tasks by WRK number or title and pick one (BUG-01, 1 Nov 2026). The results float over the page in a
 * fixed-position list in <body>, so a narrow card or the bottom of the window never cuts them off: the list is at least
 * 24rem wide, opens above the box when there is more room there, scrolls with a visible bar, and says when there are
 * more matches than it shows.
 */
export function TaskPicker({ onPick, exclude = [], placeholder = "Search WRK-… or title", autoFocus }: { onPick: (t: Item) => void; exclude?: string[]; placeholder?: string; autoFocus?: boolean }) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  const list = useRef<HTMLUListElement>(null);
  const [pos, setPos] = useState<{ left: number; width: number; top?: number; bottom?: number; maxH: number } | null>(null);
  const active = open && q.trim().length >= 2;
  const res = useQuery({ queryKey: ["task-pick", q.trim()], queryFn: () => api<{ items: Item[] }>(`/api/work/items?view=all&q=${encodeURIComponent(q.trim())}&limit=${LIMIT}`), enabled: active });
  const items = (res.data?.items ?? []).filter((x) => !exclude.includes(x.id));

  useEffect(() => {
    const h = (e: MouseEvent) => !box.current?.contains(e.target as Node) && !list.current?.contains(e.target as Node) && setOpen(false);
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);
  useLayoutEffect(() => {
    if (!active) { setPos(null); return; }
    const place = () => {
      const r = box.current?.getBoundingClientRect();
      if (!r) return;
      const margin = 12, vw = window.innerWidth, vh = window.innerHeight;
      const width = Math.min(Math.max(r.width, 384), vw - 2 * margin);
      const left = Math.max(margin, Math.min(r.left, vw - margin - width));
      const below = vh - r.bottom - margin, above = r.top - margin;
      const up = below < 240 && above > below;
      setPos({ left, width, ...(up ? { bottom: vh - r.top + 6 } : { top: r.bottom + 6 }), maxH: Math.max(160, Math.min(420, up ? above : below)) });
    };
    place();
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => { window.removeEventListener("resize", place); window.removeEventListener("scroll", place, true); };
  }, [active, items.length]);

  const pick = (t: Item) => { onPick(t); setQ(""); setOpen(false); };
  return (
    <div ref={box}>
      <input className="input" placeholder={placeholder} value={q} autoFocus={autoFocus} aria-label={placeholder} aria-expanded={active}
        onFocus={() => setOpen(true)} onChange={(e) => { setQ(e.target.value); setOpen(true); }}
        onKeyDown={(e) => { if (e.key === "Escape") { setOpen(false); (e.target as HTMLInputElement).blur(); } if (e.key === "Enter" && items.length === 1) { e.preventDefault(); pick(items[0]!); } }} />
      {active && pos && createPortal(
        <ul ref={list} role="listbox" style={{ position: "fixed", left: pos.left, width: pos.width, top: pos.top, bottom: pos.bottom, maxHeight: pos.maxH }}
          className="scroll-thin z-[100] overflow-y-auto rounded-xl border border-[var(--line)] bg-[var(--panel)] p-1 shadow-[var(--shadow-pop)]">
          {res.isPending && <li className="muted px-3 py-1.5 text-sm">Searching…</li>}
          {res.data && items.length === 0 && <li className="muted px-3 py-1.5 text-sm">No matching task.</li>}
          {items.map((x) => (
            <li key={x.id} role="option">
              <button type="button" className="flex w-full items-baseline gap-2 rounded-lg px-3 py-1.5 text-left text-sm hover:bg-[#f4f5fb]" onClick={() => pick(x)} title={`${x.ref} ${x.title}`}>
                <span className="muted num shrink-0 text-xs">{x.ref}</span>
                <span className="min-w-0 flex-1 truncate">{x.title}</span>
              </button>
            </li>
          ))}
          {res.data && res.data.items.length >= LIMIT && <li className="muted border-t border-[var(--line)] px-3 py-1.5 text-xs">First {LIMIT} matches. Type more to narrow it down.</li>}
        </ul>,
        document.body,
      )}
    </div>
  );
}
